import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatError } from "../lib/api";
import { AuthShell } from "./Login";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2 } from "lucide-react";
import { useSeo } from "../lib/seo";

export default function Register() {
  useSeo({ title: "Create account | DentiCision", path: "/register", noindex: true });
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = (params.get("next") || "").startsWith("/app") ? params.get("next") : "/app";
  const [form, setForm] = useState({ name: "", clinic_name: "", email: "", password: "", accept_terms: false });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.accept_terms) return setError("Please accept the Terms of Service, Privacy Policy and Medical Disclaimer to continue.");
    setLoading(true);
    try {
      await register(form);
      navigate(next);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Create your account" subtitle="Start your 7-day free trial — no card required">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="name">Full name</Label>
          <Input id="name" data-testid="register-name" value={form.name} onChange={set("name")} required className="mt-1.5" placeholder="Dr. Jane Doe" />
        </div>
        <div>
          <Label htmlFor="clinic">Clinic name (optional)</Label>
          <Input id="clinic" data-testid="register-clinic" value={form.clinic_name} onChange={set("clinic_name")} className="mt-1.5" placeholder="Smile Dental Clinic" />
        </div>
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" data-testid="register-email" type="email" value={form.email} onChange={set("email")} required className="mt-1.5" placeholder="you@clinic.com" />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <Input id="password" data-testid="register-password" type="password" value={form.password} onChange={set("password")} required minLength={6} className="mt-1.5" placeholder="At least 6 characters" />
        </div>
        <label className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed">
          <input type="checkbox" data-testid="register-accept-terms" checked={form.accept_terms} onChange={(e) => setForm({ ...form, accept_terms: e.target.checked })} className="mt-1 h-4 w-4 accent-[#0B2545]" />
          <span>I am a licensed dental professional and I agree to the <Link to="/terms" target="_blank" className="text-sky-600 hover:underline">Terms of Service</Link>, <Link to="/privacy" target="_blank" className="text-sky-600 hover:underline">Privacy Policy</Link> and <Link to="/medical-disclaimer" target="_blank" className="text-sky-600 hover:underline">Medical Disclaimer</Link>. I understand DentiCision is an assistive second opinion and does not replace my clinical judgment or physician consultation.</span>
        </label>
        {error && <p data-testid="register-error" className="text-sm text-red-600">{error}</p>}
        <Button type="submit" data-testid="register-submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800 h-11">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
        </Button>
      </form>
      <p className="text-sm text-slate-600 mt-5 text-center">
        Already have an account? <Link to={`/login${next !== "/app" ? `?next=${encodeURIComponent(next)}` : ""}`} className="text-sky-600 hover:underline" data-testid="to-login-link">Sign in</Link>
      </p>
    </AuthShell>
  );
}
